import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useScale } from '../../../hooks/useScale';
import { useColors } from '../../../hooks/useColors';
import theme from '../../../common/theme';

const SetModifierStyleModalStyles = () => {
  const { scale } = useScale();
  const { colors } = useColors();

  return useMemo(
    () =>
      StyleSheet.create({
        background: {
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-end',
          backgroundColor: theme.colors.modalBg,
        },
        panel: {
          width: scale(420),
          height: '100%',
          backgroundColor: colors.popupBg,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: scale(24),
          paddingBottom: scale(16),
        },
        scrollArea: {
          flex: 1,
        },
        scrollContent: {
          paddingTop: scale(8),
          paddingHorizontal: scale(24),
          paddingBottom: scale(24),
          rowGap: scale(24),
        },
        headerActions: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
        },
        headerIconButton: {
          width: scale(32),
          height: scale(32),
          borderRadius: theme.radius.s,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.inputBackground,
        },
        title: {
          color: colors.text,
          fontSize: scale(20),
          fontFamily: theme.fonts.semibold,
        },
        fieldLabelRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: scale(8),
        },
        fieldLabel: {
          color: colors.text,
          fontSize: scale(13),
          fontFamily: theme.fonts.medium,
        },
        sectionLabelSpacing: {
          marginBottom: scale(10),
        },
        counterText: {
          color: colors.textLight,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        inputContainer: {
          width: '100%',
          borderRadius: theme.radius.s,
        },
        swatchGrid: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: scale(14),
        },
        swatch: {
          flexBasis: '30%',
          flexGrow: 1,
          minHeight: scale(56),
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
          paddingVertical: scale(12),
          paddingHorizontal: scale(12),
          borderRadius: theme.radius.m,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        swatchSelected: {
          borderColor: colors.text,
        },
        swatchDot: {
          width: scale(20),
          height: scale(20),
          borderRadius: theme.radius.s,
          flexShrink: 0,
        },
        swatchLabel: {
          color: colors.text,
          fontSize: scale(12),
          lineHeight: scale(16),
          fontFamily: theme.fonts.regular,
          flexShrink: 1,
        },
        noColourRow: {
          marginTop: scale(14),
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
          paddingVertical: scale(12),
          paddingHorizontal: scale(12),
          borderRadius: theme.radius.m,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        noColourDot: {
          width: scale(20),
          height: scale(20),
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.textLight,
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
        },
        noColourSlash: {
          width: scale(26),
          height: 1,
          backgroundColor: colors.textLight,
          transform: [{ rotate: '45deg' }],
        },
        noColourTextGroup: {
          rowGap: scale(2),
        },
        noColourLabel: {
          color: colors.text,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        noColourHelper: {
          color: colors.textLight,
          fontSize: scale(11),
          fontFamily: theme.fonts.regular,
        },
        noColourRowDisabled: {
          opacity: 0.4,
        },
        italicToggleRow: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(12),
        },
        italicToggle: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(6),
          paddingVertical: scale(8),
          paddingHorizontal: scale(14),
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        italicToggleSelected: {
          borderColor: colors.text,
        },
        italicToggleLabel: {
          color: colors.text,
          fontSize: scale(13),
          fontFamily: theme.fonts.regular,
        },
        helperText: {
          color: colors.textLight,
          fontSize: scale(11),
          fontFamily: theme.fonts.regular,
          lineHeight: scale(15),
        },
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
        },
        keywordInput: {
          flex: 1,
        },
        keywordAddButton: {
          height: scale(44),
        },
        modeToggleGroup: {
          flexDirection: 'row',
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          overflow: 'hidden',
        },
        modeToggleOption: {
          flex: 1,
          alignItems: 'center',
          paddingVertical: scale(10),
          paddingHorizontal: scale(10),
          userSelect: 'none',
        },
        modeToggleOptionSelected: {
          backgroundColor: colors.elevation2,
        },
        modeToggleLabel: {
          color: colors.textLight,
          fontSize: scale(13),
          fontFamily: theme.fonts.regular,
        },
        modeToggleLabelSelected: {
          color: colors.text,
          fontFamily: theme.fonts.medium,
        },
        matchTypeGroup: {
          flexDirection: 'row',
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          overflow: 'hidden',
        },
        matchTypeOption: {
          paddingVertical: scale(8),
          paddingHorizontal: scale(10),
          userSelect: 'none',
        },
        matchTypeOptionSelected: {
          backgroundColor: colors.elevation2,
        },
        matchTypeLabel: {
          color: colors.textLight,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        matchTypeLabelSelected: {
          color: colors.text,
          fontFamily: theme.fonts.medium,
        },
        keywordList: {
          rowGap: scale(8),
        },
        keywordChip: {
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'nowrap',
          columnGap: scale(10),
          paddingVertical: scale(8),
          paddingLeft: scale(14),
          paddingRight: scale(8),
          borderRadius: theme.radius.m,
          backgroundColor: colors.inputBackground,
        },
        keywordChipText: {
          flex: 1,
          flexShrink: 1,
          color: colors.text,
          fontFamily: theme.fonts.medium,
          fontSize: scale(14),
        },
        keywordChipTextItalic: {
          fontStyle: 'italic',
        },
        keywordChipTextBackgroundPill: {
          paddingHorizontal: scale(6),
          paddingVertical: scale(2),
          borderRadius: scale(4),
          overflow: 'hidden',
        },
        keywordChipRight: {
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(8),
        },
        keywordRemove: {
          width: scale(32),
          height: scale(32),
          borderRadius: theme.radius.s,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.elevation2,
        },
        errorText: {
          color: theme.colors.red,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        actions: {
          flexDirection: 'row',
          columnGap: scale(10),
          padding: scale(24),
          borderTopWidth: 1,
          borderTopColor: colors.borderColor,
          backgroundColor: colors.popupBg,
        },
        saveButton: {
          flex: 1,
        },
        cancelButton: {
          width: scale(110),
        },
      }),
    [colors, scale],
  );
};

export default SetModifierStyleModalStyles;
